import * as React from "react";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CssBaseline,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  Collapse,
  Drawer as MuiDrawer,
  Typography,
} from "@mui/material";
import { TbLockPassword } from "react-icons/tb";
import { FiChevronUp, FiChevronDown } from "react-icons/fi";
import weblogo from "../assets/ChatGPT Image Jun 24, 2026, 06_30_58 PM.png";
import { FaUser, FaRegUser, FaCar, FaCalendarCheck, FaSchool } from "react-icons/fa";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import sidemenustroke from "../../src/assets/sidemenudashboardiconstroke.png";
import sidemenufilled from "../../src/assets/sidemenudashboardiconfilled.png";
import customerfilled from "../assets/customer filled.png";
import customerstroke from "../assets/customer fstroke.png";
import bookingstroke from "../assets/booking stroke.png";
import bookingsfilled from "../assets/booking filled.png";
import vehiclefilled from "../assets/electric-scooter filled.png";
import vehiclestroke from "../assets/electric-scooter stokes.png";
import driverstroke from "../assets/driver-man stroke.png";
import driverfilled from "../assets/driver-man filled.png";
import changepwfilled from "../assets/changepasswordfilled.png";
import changepwstroke from "../assets/changepasswordstroke.png";
import companystroke from "../assets/Company stroke.png";
import companyfilled from "../assets/Company filled.png";

import { getTenantConfigFromStorage } from "../helpers/tenantConfig";
import { TENANT_CONFIG_STORAGE_KEY } from "../helpers/tenantConfig";
import { RiDashboardFill, RiUserSettingsLine } from "react-icons/ri";
import { FaPeopleGroup } from "react-icons/fa6";

const drawerWidth = 225;
const collapsedWidth = 55;

const ACTIVE_ICON_FILTER = "brightness(0) saturate(100%) invert(47%) sepia(97%) saturate(3539%) hue-rotate(187deg) brightness(99%) contrast(101%)";

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  background: "linear-gradient(180deg, #072A7A 0%, #0432A5 50%, #001B5E 100%)",
  color: "#FFFFFF",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: collapsedWidth,
  [theme.breakpoints.up("sm")]: {
    width: collapsedWidth,
  },
  background: "linear-gradient(180deg, #072A7A 0%, #0432A5 50%, #001B5E 100%)",
  color: "#FFFFFF",
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open
    ? {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": {
          ...openedMixin(theme),
          overflowX: "hidden",
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          borderRight: "1px solid #FFFFFF14",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        },
      }
    : {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": {
          ...closedMixin(theme),
          overflowX: "hidden",
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          borderRight: "1px solid #FFFFFF14",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        },
      }),
}));

const Sidemenu = ({
  open,
  onClose,
  onOpen,
}: {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();

  const [menuOpenStates, setMenuOpenStates] = React.useState<{ [key: string]: boolean }>({});

  const userString = localStorage.getItem("user");
  const user: any = userString ? JSON.parse(userString) : null;

  const role = user?.role;
  const tenantConfig = getTenantConfigFromStorage();
  const parentLabel = tenantConfig.labels.parent;
  const schoolLabel = tenantConfig.labels.school;
  const isCompanyTenant = tenantConfig.mode === "company";
  const companyListingPath = isCompanyTenant ? "/company-listing" : "/school-listing";
  const addCompanyPath = isCompanyTenant ? "/add-new-company" : "/add-new-school";
  const isSuperAdmin = role === "superAdmin";
  const isSchoolAdmin = role === "schoolAdmin";
  const canViewModules = isSuperAdmin || role === "admin" || isSchoolAdmin;
  const canEditModules = role === "admin";

  const menuItems: any[] = [
    {
      text: "Dashboard",
      path: "/dashboard",
      renderIcon: (isActive: boolean) =>
        isActive ? (
          <RiDashboardFill size={18} color="#FFFFFF" />
        ) : (
          <RiDashboardFill size={18} color="#DCE7FF" />
        ),
    },
    isSuperAdmin && {
      text: "Tenant",
      path: "/tenant-list",
      renderIcon: (isActive: boolean) =>
        isActive ? (
          <FaUser size={18} color="#FFFFFF" />
        ) : (
          <FaRegUser size={18} color="#DCE7FF" />
        ),
      children: [
        { text: "Tenant List", path: "/tenant-list" },
        { text: "Create Tenant", path: "/create-tenant" },
      ].filter(Boolean),
    },
    canViewModules && {
      text: "Vehicles",
      path: "/vehicle-list",
      renderIcon: (isActive: boolean) =>
        isActive ? (
          <FaCar size={18} color="#FFFFFF" />
        ) : (
          <FaCar size={18} color="#DCE7FF" />
        ),
      children: [
        { text: "Vehicle List", path: "/vehicle-list" },
        canEditModules && { text: "Add Vehicle", path: "/add-vehicle" },
      ].filter(Boolean),
    },
    canViewModules && {
      text: "Drivers",
      path: "/driver-list",
      renderIcon: (isActive: boolean) =>
        isActive ? (
          <RiUserSettingsLine size={18} color="#FFFFFF" />
        ) : (
          <RiUserSettingsLine size={18} color="#DCE7FF" />
        ),
      children: [
        { text: "Driver List", path: "/driver-list" },
        canEditModules && { text: "Add Driver", path: "/add-driver" },
      ].filter(Boolean),
    },
    canViewModules && {
      text: "Bookings",
      path: "/booking-list",
      renderIcon: (isActive: boolean) =>
        isActive ? (
          <FaCalendarCheck size={18} color="#FFFFFF" />
        ) : (
          <FaCalendarCheck size={18} color="#DCE7FF" />
        ),
      children: [
        { text: "Booking List", path: "/booking-list" },
        { text: "See All Van", path: "/all-van" },
        canEditModules && { text: "Add Booking", path: "/add-booking" },
      ].filter(Boolean),
    },
    canViewModules && {
      text: schoolLabel,
      path: companyListingPath,
      renderIcon: (isActive: boolean) =>
        isActive ? (
          <FaSchool size={18} color="#FFFFFF" />
        ) : (
          <FaSchool size={18} color="#DCE7FF" />
        ),
      children: [
        { text: `${schoolLabel} List`, path: companyListingPath },
        canEditModules && { text: `Add ${schoolLabel}`, path: addCompanyPath },
      ].filter(Boolean),
    },
    canViewModules && {
      text: `${parentLabel}s`,
      path: "/parent-list",
     renderIcon: (isActive: boolean) =>
        isActive ? (
          <FaPeopleGroup size={18} color="#FFFFFF" />
        ) : (
          <FaPeopleGroup size={18} color="#DCE7FF" />
        ),
      children: [
        { text: `${parentLabel} Management`, path: "/parent-list" },
        canEditModules && { text: `Add ${parentLabel}`, path: "/add-parent" },
      ].filter(Boolean),
    },
    canViewModules && {
      text: "Users",
      path: "/user",
      renderIcon: (isActive: boolean) =>
        isActive ? (
          <FaUser size={18} color="#FFFFFF" />
        ) : (
          <FaRegUser size={18} color="#DCE7FF" />
        ),
      children: [
        { text: "User List", path: "/user-list" },
        canEditModules && { text: "Add User", path: "/addUser" },
      ].filter(Boolean),
    },
    canViewModules && !isSchoolAdmin && {
      text: "Change Password",
      path: "/change-password",
       renderIcon: (isActive: boolean) =>
        isActive ? (
          <TbLockPassword size={18} color="#FFFFFF" />
        ) : (
          <TbLockPassword size={18} color="#DCE7FF" />
        ),
    },
  ].filter(Boolean);

  const getActiveItem = (item: any) => {
    if (item.path === location.pathname) return true;
    if (item.children) {
      return item.children.some((child: any) => child.path === location.pathname);
    }
    return false;
  };

  React.useEffect(() => {
    const openMenus: { [key: string]: boolean } = {};
    menuItems.forEach((item) => {
      if (item.children) {
        const childActive = item.children.some(
          (child: any) => child.path === location.pathname
        );
        if (childActive) {
          openMenus[item.text] = true;
        }
      }
    });
    setMenuOpenStates((prev) => ({ ...prev, ...openMenus }));
  }, [location.pathname]);

  const handleDrawerOpen = () => {
    onOpen();
  };

  const handleDrawerClose = () => {
    onClose();
    setMenuOpenStates({});
  };

  const toggleMenu = (text: string) => {
    if (!open) {
      onOpen();
      setMenuOpenStates((prev) => ({ ...prev, [text]: true }));
    } else {
      setMenuOpenStates((prev) => ({ ...prev, [text]: !prev[text] }));
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      <Drawer variant={isMobile ? "temporary" : "permanent"} open={open}>
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: open ? "initial" : "center",
              px: open ? 2.5 : 0,
              height: 65,
  //             background:
  // "linear-gradient(180deg, #072A7A 0%, #0432A5 50%, #001B5E 100%)",
              gap: 1.5,
            }}
          >
            <Box
              component="img"
              src={weblogo}
              alt="Logo"
              sx={{
                width: open ? 60 : 36,
                height: open ? 40 : 36,
                objectFit: "contain",
                transition: "all 0.2s ease"
              }}
            />
            {open && (
              <Box>
                <Typography sx={{ marginBottom: .5, fontWeight: 600, fontSize: 16, color: "#FFFFFF", lineHeight: 1.1, fontFamily: "'Poppins', 'Inter', sans-serif" }}>
                  Van Wala
                </Typography>
                <Typography sx={{ fontSize: 10, color: "#D8E4FF", fontWeight: 400, fontFamily: "'Poppins', 'Inter', sans-serif" }}>
                  Operations System
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ borderColor: "#FFFFFF14", mx: 2, mb: 0.5 }} />

          <List sx={{ paddingTop: 0.5, px: open ? 1.5 : 0.8 }}>
            {menuItems.map((item) => {
              if (!item) return null;
              const hasChildren = item.children && item.children.length > 0;
              const isParentActive = getActiveItem(item);
              const isSubmenuOpen = menuOpenStates[item.text];
              const isLeafActive = item.path === location.pathname;
              const isItemActive = !hasChildren && isLeafActive;

              return (
                <React.Fragment key={item.text}>
                  <ListItem disablePadding sx={{ display: "block" }}>
                    <ListItemButton
                      onClick={() => {
                        if (!open && hasChildren) {
                          onOpen();
                          setMenuOpenStates((prev) => ({ ...prev, [item.text]: true }));
                          navigate(item.children[0].path);
                        } else if (hasChildren) {
                          toggleMenu(item.text);
                        } else {
                          navigate(item.path);
                          if (isMobile) handleDrawerClose();
                        }
                      }}
                      sx={{
                        minHeight: 46,
                        justifyContent: open ? "initial" : "center",
                        px: open ? 2.2 : 1.2,
                        mx: open ? 1.2 : 0.6,
                        mb: 0.8,
                        borderRadius: "8px",
                        whiteSpace: "nowrap",
                        background: (isItemActive || isParentActive) ? "linear-gradient(135deg, #1E63FF 0%, #0B4EE8 100%)" : "transparent",
                        boxShadow: (isItemActive || isParentActive) ? "0 4px 14px rgba(30, 99, 255, 0.45)" : "none",
                        color: (isItemActive || isParentActive) ? "#FFFFFF" : "#D8E4FF",
                        position: (isItemActive || isParentActive) ? "relative" : "static",
                        "&::before": (isItemActive || isParentActive) ? {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: "10px",
                          bottom: "10px",
                          width: "3px",
                          borderRadius: "0 4px 4px 0",
                          backgroundColor: "#FFFFFF",
                        } : {},
                        "&:hover": {
                          backgroundColor: (isItemActive || isParentActive) ? undefined : "#FFFFFF14",
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 2.5 : "auto",
                          justifyContent: "center",
                        }}
                      >
                        {item.renderIcon ? item.renderIcon(isItemActive || isParentActive) : null}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        sx={{
                          opacity: open ? 1 : 0,
                          transition: "opacity 0.2s ease",
                        }}
                        primaryTypographyProps={{
                          fontSize: "13.5px",
                          fontWeight: (isItemActive || isParentActive) ? 600 : 500,
                          color: (isItemActive || isParentActive) ? "#FFFFFF" : "#D8E4FF",
                          fontFamily: "'Poppins', 'Inter', sans-serif",
                        }}
                      />
                      {hasChildren && open && (
                        isSubmenuOpen ? (
                          <FiChevronUp style={{ color: "#8BA3C7", fontSize: 16, flexShrink: 0 }} />
                        ) : (
                          <FiChevronDown style={{ color: "#8BA3C7", fontSize: 16, flexShrink: 0 }} />
                        )
                      )}
                    </ListItemButton>

                    {hasChildren && (
                      <Collapse in={isSubmenuOpen && open} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ pl: 1 }}>
                          {item.children.map((child: any) => {
                            const isChildActive = child.path === location.pathname;
                            return (
                              <ListItemButton
                                key={child.text}
                                sx={{
                                  pl: 3.2,
                                  pr: 1.5,
                                  py: 0.7,
                                  mx: 1.5,
                                  mb: 0.5,
                                  minHeight: 36,
                                  borderRadius: "8px",
                                  justifyContent: "initial",
                                  background: isChildActive ? "rgba(30, 99, 255, 0.2)" : "transparent",
                                  color: isChildActive ? "#FFFFFF" : "#DCE7FF",
                                  position: isChildActive ? "relative" : "static",
                                  "&::before": isChildActive ? {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: "8px",
                                    bottom: "8px",
                                    width: "3px",
                                    borderRadius: "0 4px 4px 0",
                                    backgroundColor: "#FFFFFF",
                                  } : {},
                                  "&:hover": {
                                    background: isChildActive ? undefined : "rgba(255, 255, 255, 0.08)",
                                    color: "#FFFFFF",
                                  },
                                }}
                                onClick={() => {
                                  navigate(child.path);
                                  if (isMobile) handleDrawerClose();
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 0,
                                    mr: 1.5,
                                    justifyContent: "center",
                                  }}
                                >
                                  {isChildActive ? (
                                    <Box
                                      sx={{
                                        width: 5,
                                        height: 5,
                                        borderRadius: "50%",
                                        backgroundColor: "#FFFFFF",
                                      }}
                                    />
                                  ) : (child.text.toLowerCase().includes("add") || child.text.toLowerCase().includes("create")) ? (
                                    <AddCircleOutlineIcon sx={{ fontSize: 13, color: "#DCE7FF" }} />
                                  ) : (
                                    <Box
                                      sx={{
                                        width: 4,
                                        height: 4,
                                        borderRadius: "50%",
                                        backgroundColor: "#DCE7FF",
                                      }}
                                    />
                                  )}
                                </ListItemIcon>
                                <ListItemText
                                  primary={child.text}
                                  primaryTypographyProps={{
                                    fontSize: "13px",
                                    fontWeight: isChildActive ? 500 : 400,
                                    fontFamily: "'Poppins', 'Inter', sans-serif",
                                    color: isChildActive ? "#FFFFFF" : "#C8D6F0",
                                    maxWidth: "125px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                />
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </Collapse>
                    )}
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        </Box>

        {open && (
          <Box
            sx={{
              mt: "auto",
              p: 1.5,
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-end",
              width: "100%",
            }}
          >
            <Box
              component="img"
              src={weblogo}
              alt="Bottom Logo Vector"
              sx={{
                width: "90%",
                maxHeight: 120,
                objectFit: "contain",
                opacity: 0.9,
                mb: 1,
              }}
            />
          </Box>
        )}
      </Drawer>

    </Box>
  );
};

export default Sidemenu;